/*
Vehicle Publishing Queue Management Hook
Core queue system for sequential vehicle publishing with state management
Author: Assistant
Date: 2025-06-24
*/

import { useState, useCallback, useRef, useEffect } from 'react'

import {
  QueueState,
  QueueItem,
  QueueVehicleItem,
  QueueItemResult,
  QueueEventCallbacks,
  QueueOperations,
  DEFAULT_QUEUE_SETTINGS,
} from '../interfaces/VehiclePublishQueue'
import {
  calculateQueueStatistics,
  determineQueueStatus,
  calculateProgress,
  generateQueueItemId,
  createInitialQueueState,
  getNextPendingItemIndex,
  getRetryableItems,
  updateQueueItem,
  validateQueueItem,
  saveQueueToStorage,
  loadQueueFromStorage,
  clearQueueFromStorage,
} from '../utils/queueUtils'

export const useVehiclePublishQueue = (
  callbacks?: QueueEventCallbacks
): {
  queueState: QueueState
  operations: QueueOperations
  progress: number
  isProcessing: boolean
  canProcess: boolean
} => {
  // Initialize state from localStorage if available
  const [queueState, setQueueState] = useState<QueueState>(() => {
    const savedState = loadQueueFromStorage()

    if (savedState && savedState.items.length > 0) {
      // Reset processing status for items that were interrupted
      const resetItems = savedState.items.map((item) =>
        item.status === 'processing' ? { ...item, status: 'pending' as const } : item
      )

      return {
        ...savedState,
        items: resetItems,
        status: determineQueueStatus(resetItems, 'idle'),
        currentIndex: -1,
        statistics: calculateQueueStatistics(resetItems, savedState.statistics.startTime),
      }
    }

    return createInitialQueueState()
  })

  // Processing control ref
  const processingRef = useRef<{
    isActive: boolean
    shouldStop: boolean
    currentPromise: Promise<void> | null
  }>({
    isActive: false,
    shouldStop: false,
    currentPromise: null,
  })

  // State ref to avoid stale closures in async processing
  const queueStateRef = useRef<QueueState>(queueState)

  // Update state ref whenever state changes
  useEffect(() => {
    queueStateRef.current = queueState
  }, [queueState])

  // Save to localStorage whenever state changes
  useEffect(() => {
    saveQueueToStorage(queueState)
  }, [queueState])

  // Calculate derived values
  const progress = calculateProgress(queueState.statistics)
  const isProcessing = queueState.status === 'processing'
  const canProcess = queueState.items.some((item) => item.status === 'pending') && !isProcessing

  /**
   * Add vehicles to the queue
   */
  const addItems = useCallback((vehicles: QueueVehicleItem[]) => {
    const newItems: QueueItem[] = vehicles.map((vehicle) => {
      const validationErrors = validateQueueItem(vehicle)

      if (validationErrors.length > 0) {
        throw new Error(`Invalid queue item: ${validationErrors.join(', ')}`)
      }

      return {
        id: generateQueueItemId(),
        vehicleId: vehicle.vehicleId,
        vehicle: vehicle.vehicle,
        branch: vehicle.branch,
        formData: vehicle.formData,
        status: 'pending',
        timestamp: {
          queued: new Date(),
        },
        retryCount: 0,
        maxRetries: DEFAULT_QUEUE_SETTINGS.maxRetries,
        operation: 'publish' as const,
        platforms: ['mercadolibre', 'chileautos', 'yapo'] as const,
        platform: 'all' as const,
      }
    })

    setQueueState((prev) => {
      const updatedItems = [...prev.items, ...newItems]
      const updatedStatistics = calculateQueueStatistics(updatedItems, prev.statistics.startTime)

      return {
        ...prev,
        items: updatedItems,
        statistics: updatedStatistics,
        status: determineQueueStatus(updatedItems, prev.status),
      }
    })
  }, [])

  /**
   * Remove a specific item from the queue
   */
  const removeItem = useCallback((itemId: string) => {
    setQueueState((prev) => {
      const updatedItems = prev.items.filter((item) => item.id !== itemId)
      const updatedStatistics = calculateQueueStatistics(updatedItems, prev.statistics.startTime)

      return {
        ...prev,
        items: updatedItems,
        statistics: updatedStatistics,
        status: determineQueueStatus(updatedItems, prev.status),
      }
    })
  }, [])

  /**
   * Clear all items from the queue
   */
  const clearQueue = useCallback(() => {
    // Stop processing if active
    processingRef.current.shouldStop = true

    setQueueState(createInitialQueueState())
    clearQueueFromStorage()
  }, [])

  /**
   * Update queue item status and metadata
   */
  const updateItemStatus = useCallback(
    (itemId: string, status: QueueItem['status'], result?: QueueItemResult, error?: string) => {
      setQueueState((prev) => {
        const now = new Date()
        const updatedItems = updateQueueItem(prev.items, itemId, {
          status,
          result,
          error,
          timestamp: {
            queued: prev.items.find((item) => item.id === itemId)?.timestamp.queued || new Date(),
            started: status === 'processing' ? now : prev.items.find((item) => item.id === itemId)?.timestamp.started,
            completed: ['completed', 'failed', 'cancelled'].includes(status) ? now : undefined,
          },
        })

        const updatedStatistics = calculateQueueStatistics(updatedItems, prev.statistics.startTime)
        const newStatus = determineQueueStatus(updatedItems, prev.status)

        return {
          ...prev,
          items: updatedItems,
          statistics: updatedStatistics,
          status: newStatus,
        }
      })
    },
    []
  )

  /**
   * Process the queue sequentially
   * This is a placeholder - will be implemented in Phase 3 with actual publishing logic
   */
  const startProcessing = useCallback(async () => {
    if (isProcessing || !canProcess) {
      return
    }

    // Mark processing as active
    processingRef.current.isActive = true
    processingRef.current.shouldStop = false

    // Update queue status to processing
    setQueueState((prev) => ({
      ...prev,
      status: 'processing',
      statistics: {
        ...prev.statistics,
        startTime: prev.statistics.startTime || new Date(),
      },
    }))

    // Placeholder processing logic - will be replaced in Phase 3
    const processNextItem = async (): Promise<void> => {
      const currentState = queueStateRef.current
      const nextIndex = getNextPendingItemIndex(currentState.items)

      if (nextIndex === -1 || processingRef.current.shouldStop) {
        // No more items to process or stopped
        processingRef.current.isActive = false
        setQueueState((prev) => ({
          ...prev,
          status: 'completed',
          statistics: {
            ...prev.statistics,
            endTime: new Date(),
          },
        }))
        callbacks?.onQueueComplete?.(currentState.statistics)

        return
      }

      const item = currentState.items[nextIndex]

      // Update item to processing
      updateItemStatus(item.id, 'processing')
      callbacks?.onItemStart?.(item)

      try {
        // Simulate processing - will be replaced with actual publishing in Phase 3
        await new Promise((resolve) => setTimeout(resolve, 1000))

        // Simulate success/failure - will be replaced with actual result in Phase 3
        const mockResult: QueueItemResult = {
          success: true,
          inventoryIdentifier: `MOCK_${Date.now()}`,
          message: 'Mock publication successful',
        }

        updateItemStatus(item.id, 'completed', mockResult)
        callbacks?.onItemComplete?.(item, mockResult)
        callbacks?.onProgress?.(calculateProgress(currentState.statistics))
      } catch (error: any) {
        const errorMessage = error.message || 'Unknown error occurred'
        updateItemStatus(item.id, 'failed', undefined, errorMessage)
        callbacks?.onItemFailed?.(item, errorMessage)
      }

      // Add delay between items
      if (!processingRef.current.shouldStop && currentState.settings.delayBetweenItems > 0) {
        await new Promise((resolve) => setTimeout(resolve, currentState.settings.delayBetweenItems))
      }

      // Process next item
      if (!processingRef.current.shouldStop) {
        await processNextItem()
      }
    }

    // Start processing
    const processingPromise = processNextItem()
    processingRef.current.currentPromise = processingPromise

    try {
      await processingPromise
    } catch (error) {
      console.error('Queue processing error:', error)
    } finally {
      processingRef.current.isActive = false
      processingRef.current.currentPromise = null
    }
  }, [isProcessing, canProcess, updateItemStatus, callbacks])

  /**
   * Pause processing
   */
  const pauseProcessing = useCallback(() => {
    processingRef.current.shouldStop = true
    setQueueState((prev) => ({ ...prev, status: 'paused' }))
    callbacks?.onQueuePaused?.()
  }, [callbacks])

  /**
   * Resume processing
   */
  const resumeProcessing = useCallback(async () => {
    if (queueState.status === 'paused') {
      callbacks?.onQueueResumed?.()
      await startProcessing()
    }
  }, [queueState.status, startProcessing, callbacks])

  /**
   * Cancel processing
   */
  const cancelProcessing = useCallback(() => {
    processingRef.current.shouldStop = true

    // Mark all pending items as cancelled
    setQueueState((prev) => {
      const updatedItems = prev.items.map((item) =>
        item.status === 'pending' || item.status === 'processing' ? { ...item, status: 'cancelled' as const } : item
      )

      return {
        ...prev,
        items: updatedItems,
        status: 'cancelled',
        statistics: calculateQueueStatistics(updatedItems, prev.statistics.startTime),
      }
    })

    callbacks?.onQueueCancelled?.()
  }, [callbacks])

  /**
   * Retry failed items
   */
  const retryFailedItems = useCallback(() => {
    setQueueState((prev) => {
      const retryableItems = getRetryableItems(prev.items)
      const updatedItems = prev.items.map((item) => {
        if (retryableItems.includes(item)) {
          return {
            ...item,
            status: 'pending' as const,
            retryCount: item.retryCount + 1,
            error: undefined,
            result: undefined,
          }
        }

        return item
      })

      return {
        ...prev,
        items: updatedItems,
        statistics: calculateQueueStatistics(updatedItems, prev.statistics.startTime),
        status: determineQueueStatus(updatedItems, prev.status),
      }
    })
  }, [])

  /**
   * Retry specific item
   */
  const retryItem = useCallback((itemId: string) => {
    setQueueState((prev) => {
      const item = prev.items.find((i) => i.id === itemId)

      if (!item || item.status !== 'failed' || item.retryCount >= item.maxRetries) {
        return prev
      }

      const updatedItems = updateQueueItem(prev.items, itemId, {
        status: 'pending',
        retryCount: item.retryCount + 1,
        error: undefined,
        result: undefined,
      })

      return {
        ...prev,
        items: updatedItems,
        statistics: calculateQueueStatistics(updatedItems, prev.statistics.startTime),
        status: determineQueueStatus(updatedItems, prev.status),
      }
    })
  }, [])

  /**
   * Update queue settings
   */
  const updateSettings = useCallback((newSettings: Partial<typeof DEFAULT_QUEUE_SETTINGS>) => {
    setQueueState((prev) => ({
      ...prev,
      settings: { ...prev.settings, ...newSettings },
    }))
  }, [])

  /**
   * Reset queue to initial state
   */
  const resetQueue = useCallback(() => {
    processingRef.current.shouldStop = true
    setQueueState(createInitialQueueState())
    clearQueueFromStorage()
  }, [])

  // Queue operations object
  const operations: QueueOperations = {
    addItems,
    removeItem,
    clearQueue,
    startProcessing,
    pauseProcessing,
    resumeProcessing,
    cancelProcessing,
    retryFailedItems,
    retryItem,
    updateSettings,
    resetQueue,
  }

  return {
    queueState,
    operations,
    progress,
    isProcessing,
    canProcess,
  }
}
